import { translateLabel } from "@/i18n/messages";
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeftIcon,
  RulerIcon,
  MountainsIcon,
  TrendUpIcon,
  WaveSineIcon,
  HeartIcon,
  UploadSimpleIcon,
  TrashIcon,
  PathIcon,
  CheckCircleIcon,
  ImagesIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, DIFFICULTY, STYLE_LABELS } from "@/lib/apiClient";
import { geometrySourceMeta, knownValue, trailAttributeLabel } from "@/lib/trailPresentation";
import { AppShell } from "@/components/layout/AppShell";
import { MapView } from "@/components/MapView";
import { TrailCommunityRating } from "@/components/TrailCommunityRating";
import { ShareMenu } from "@/components/ShareMenu";
import { trailSharePayload } from "@/lib/share";
import { parkPath, trailPath } from "@/lib/routes";
import { CommunityMediaGallery } from "@/components/CommunityMediaGallery";
import { SuggestEdit } from "@/components/SuggestEdit";
import { ReportContent } from "@/components/ReportContent";
import { useAuth } from "@/context/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { trackEvent } from "@/lib/analytics";
import { Seo, buildTrailSchema } from "@/components/Seo";
import { TrailStatusTrust } from "@/components/StatusTrust";

export default function TrailDetail() {
  const { destinationId, trailId, id } = useParams();
  const navigate = useNavigate();
  const routeTrailId = destinationId && trailId ? `${destinationId}/${trailId}` : id;
  const { isFavorite, toggleFavorite, user } = useAuth();
  const [trail, setTrail] = useState(null);
  const [trailHero, setTrailHero] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const viewedTrailIdRef = useRef(null);

  const uploadGpx = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(`/trails/${trail?.id || routeTrailId}/gpx`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTrail((current) => ({
        ...current,
        path: data.path,
        path_source: data.path_source || "gpx",
        path_verified: true,
        path_provider: null,
        path_source_url: null,
        path_observed_at: null,
      }));
      toast.success(`Verified GPS track uploaded (${data.point_count} points)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeGpx = async () => {
    try {
      const { data } = await api.delete(`/trails/${trail?.id || routeTrailId}/gpx`);
      setTrail((current) => ({
        ...current,
        path: data.path,
        path_source: data.path_source,
        path_verified: false,
        path_provider: null,
        path_source_url: null,
        path_observed_at: null,
      }));
      toast(translateLabel("GPS track removed"));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not remove");
    }
  };

  useEffect(() => {
    let cancelled = false;
    window.scrollTo(0, 0);
    setTrail(null);
    setTrailHero(null);

    api.get(`/trails/${routeTrailId}`).then(async ({ data }) => {
      if (cancelled) return;
      setTrail(data);
      if (viewedTrailIdRef.current !== data.id) {
        viewedTrailIdRef.current = data.id;
        trackEvent("trail_view", {
          trail_id: String(data.id),
          trail_name: data.name || "",
          park_id: data.park?.id ? String(data.park.id) : "",
          park_name: data.park?.name || "",
          difficulty: data.difficulty || "",
          riding_style: data.riding_style || "",
        });
      }
      const canonicalPath = trailPath(data);
      const currentPath = destinationId && trailId
        ? `/trail/${destinationId}/${trailId}`
        : `/trail/${id}`;
      if (canonicalPath !== currentPath) {
        navigate(canonicalPath, { replace: true });
      }

      try {
        const mediaResult = await api.get("/media", {
          params: { destination_id: data.park.id, trail_id: data.id, filter: "PHOTOS" },
        });
        if (cancelled) return;
        setTrailHero(mediaResult.data?.hero || mediaResult.data?.items?.[0] || null);
      } catch {
        // Trail media is optional; the detail page remains complete without a hero photo.
      }
    }).catch(() => {
      if (cancelled) return;
      toast.error(translateLabel("Trail not found"));
      navigate("/");
    });

    return () => { cancelled = true; };
  }, [routeTrailId, navigate, destinationId, trailId, id]);

  if (!trail) {
    return (
      <AppShell size="wide" innerClassName="mx-auto w-full max-w-6xl" contentClassName="space-y-4">
        <Skeleton className="h-10 w-40 bg-white/5" />
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="space-y-4">
            <Skeleton className="h-16 w-3/4 bg-white/5" />
            <Skeleton className="h-28 w-full bg-white/5" />
          </div>
          <Skeleton className="h-72 w-full rounded-2xl bg-white/5" />
        </div>
      </AppShell>
    );
  }

  const canonicalPath = trailPath(trail);
  const seoDescription = trail.description || `${trail.name} is a ${trail.difficulty || "mountain bike"} trail at ${trail.park?.name || "Bikeparks Finder"}.`;
  const seoImage = trailHero?.url || trail.park?.image;

  const difficulty = DIFFICULTY[trail.difficulty] || DIFFICULTY.unknown;
  const geometry = geometrySourceMeta(trail.path_source, trail.path_verified, trail.path_provider);
  const geometryProviderLabel = trail.path_provider === "TRAILFORKS" ? "Trailforks" : trail.path_provider;
  const favorite = isFavorite(trail.id, "trail");
  const attributes = trail.attributes || trail.features || [];
  const canManageGeometry = Boolean(user && ["ADMIN", "SUPER_ADMIN"].includes(user.role));
  const handleBack = () => {
    const historyIndex = window.history.state?.idx;
    if (typeof historyIndex === "number" && historyIndex > 0) navigate(-1);
    else navigate(parkPath(trail.park), { replace: true });
  };

  const stats = [
    { label: "Length", value: knownValue(trail.length_km, (value) => `${value} km`), icon: RulerIcon },
    { label: "Elevation gain", value: knownValue(trail.elevation_gain_m, (value) => `${Math.round(value)} m`), icon: MountainsIcon },
    { label: "Elevation loss", value: knownValue(trail.elevation_loss_m, (value) => `${Math.round(value)} m`), icon: MountainsIcon },
    { label: "Trail type", value: knownValue(trail.trail_type), icon: TrendUpIcon },
    { label: "Riding style", value: knownValue(trail.riding_style, (value) => STYLE_LABELS[value] || value), icon: WaveSineIcon },
    { label: "Surface", value: knownValue(trail.surface), icon: PathIcon },
  ];

  const handleFavorite = async () => {
    const result = await toggleFavorite(trail.id, "trail");
    if (result.needAuth) {
      toast(translateLabel("Sign in to save"));
      navigate("/login");
    } else if (result.added) {
      trackEvent("favorite_added", { content_type: "trail", item_id: String(trail.id), item_name: trail.name || "" });
      toast.success(translateLabel("Trail saved"));
    } else {
      trackEvent("favorite_removed", { content_type: "trail", item_id: String(trail.id), item_name: trail.name || "" });
      toast(translateLabel("Removed"));
    }
  };

  return (
    <AppShell size="wide" innerClassName="mx-auto w-full max-w-6xl">
      <Seo
        title={`${trail.name} trail — ${trail.park?.name || "Bikeparks Finder"}`}
        description={seoDescription}
        path={canonicalPath}
        image={seoImage}
        structuredData={buildTrailSchema(trail, canonicalPath, seoImage)}
      />
      <section className="grid gap-5 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-stretch">
        <div className="flex items-center justify-between gap-3 lg:col-span-2">
          <button
            onClick={handleBack}
            data-testid="back-btn"
            className="flex shrink-0 items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-white/10"
          >
            <ArrowLeftIcon size={16} weight="bold" /> {translateLabel("Back to trails")}
          </button>

          <div className="flex min-w-0 flex-wrap justify-end gap-2">
            <span
              className="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold"
              style={{ backgroundColor: `${difficulty.color}22`, color: difficulty.color }}
            >
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: difficulty.color }} /> {translateLabel(difficulty.label)}
            </span>
          </div>
        </div>

        <div className="min-w-0 lg:col-start-1 lg:row-start-2">
          <h1 className="font-display text-4xl font-black uppercase leading-[0.92] tracking-tight text-white sm:text-5xl md:text-6xl" data-testid="trail-title">
            {trail.name}
          </h1>
          <Link
            to={parkPath(trail.park)}
            className="mt-3 inline-block text-sm font-semibold text-[#CCFF00] hover:underline"
            data-testid="trail-park-link"
          >
            {trail.park.name} · {[trail.park.city, translateLabel(trail.park.country)].filter(Boolean).join(", ")}
          </Link>

          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid="trail-status-inline">
            <TrailStatusTrust trail={trail} compact />
            {trail.operational_status_verification?.source?.url && (
              <a
                href={trail.operational_status_verification.source.url}
                target="_blank"
                rel="noreferrer"
                className="text-[10px] font-bold text-zinc-500 underline decoration-zinc-700 underline-offset-4 hover:text-[#CCFF00]"
              >
                {translateLabel("Status source")}
              </a>
            )}
          </div>
        </div>

        {trailHero ? (
          <a
            href="#community-media"
            className="group relative min-h-64 overflow-hidden rounded-2xl border border-white/10 bg-[#121214] lg:col-start-2 lg:row-start-2 lg:row-span-3 lg:min-h-[320px]"
            data-testid="trail-hero-media"
          >
            <img
              src={trailHero.url}
              alt={trailHero.title || `${trail.name} trail media`}
              className="h-full min-h-64 w-full object-cover transition-transform duration-500 group-hover:scale-[1.02] lg:min-h-[320px]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-black/10" />
            <span className="absolute bottom-4 right-4 flex items-center gap-1.5 rounded-full border border-white/15 bg-black/70 px-3 py-2 text-xs font-bold text-white">
              <ImagesIcon size={15} /> {translateLabel("View trail media")}
            </span>
          </a>
        ) : (
          <div
            className="relative flex min-h-44 overflow-hidden rounded-2xl border border-white/[0.08] bg-[radial-gradient(circle_at_75%_20%,rgba(204,255,0,0.12),transparent_32%),linear-gradient(135deg,#151517,#0c0c0e)] p-5 sm:min-h-56 lg:col-start-2 lg:row-start-2 lg:row-span-3 lg:min-h-[320px]"
            data-testid="trail-hero-fallback"
          >
            <div className="absolute -right-8 -top-10 text-white/[0.035]">
              <PathIcon size={220} weight="duotone" />
            </div>
            <div className="relative mt-auto max-w-md">
              <div className="label-tiny text-[#CCFF00]">{translateLabel("Trail profile")}</div>
              <div className="mt-2 font-display text-2xl font-bold uppercase text-white">{translateLabel(difficulty.label)} · {translateLabel(STYLE_LABELS[trail.riding_style] || trail.riding_style || trail.trail_type || "Trail")}</div>
              <p className="mt-2 text-sm leading-relaxed text-zinc-400">
                {translateLabel("No verified trail photo yet. The map and verified ride data below remain the primary source for this line.")}
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 items-stretch gap-2 sm:flex sm:flex-wrap sm:items-stretch sm:gap-2.5 lg:col-start-1 lg:row-start-3">
          <button
            onClick={handleFavorite}
            data-testid="trail-fav-btn"
            className={`flex h-12 items-center justify-center gap-2 rounded-xl px-4 text-sm font-bold transition-transform active:scale-95 ${
              favorite ? "bg-[#FF4500] text-white" : "bg-[#CCFF00] text-black"
            }`}
          >
            <HeartIcon size={18} weight={favorite ? "fill" : "regular"} /> {translateLabel(favorite ? "Saved" : "Save trail")}
          </button>

          <ShareMenu payload={trailSharePayload(trail)} compact className="h-12 w-full rounded-xl sm:w-auto" />

          <div className="col-span-2 min-w-0 sm:contents">
            <TrailCommunityRating trailId={trail.id} variant="inline" />
          </div>

          {trailHero && (
            <a
              href="#community-media"
              className="col-span-2 flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-bold text-white hover:bg-white/10 sm:col-span-1 sm:px-5"
            >
              <ImagesIcon size={18} /> {translateLabel("View photos")}
            </a>
          )}
        </div>

        <p className="max-w-2xl text-base leading-relaxed text-zinc-300 lg:col-start-1 lg:row-start-4">
          {trail.description || translateLabel("No trail description is available yet.")}
        </p>
      </section>

      <div className="my-7 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 md:grid-cols-3 lg:grid-cols-6" data-testid="trail-decision-stats">
        {stats.map((item) => (
          <div key={translateLabel(item.label)} className="min-w-0 bg-[#121214] p-4">
            <div className="mb-2 text-[#CCFF00]"><item.icon size={16} weight="bold" /></div>
            <div className="label-tiny">{translateLabel(item.label)}</div>
            <div className="mt-1 break-words font-display text-base font-bold capitalize text-white">{item.value}</div>
          </div>
        ))}
      </div>

      <section className="mb-8">
        <h2 className="mb-3 font-display text-2xl font-bold uppercase text-white">{translateLabel("Trail characteristics")}</h2>
        {attributes.length > 0 ? (
          <div className="flex flex-wrap gap-2" data-testid="trail-features">
            {attributes.map((attribute) => (
              <span key={attribute} className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white">
                {trailAttributeLabel(attribute)}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-zinc-500" data-testid="trail-features-empty">{translateLabel("No structured trail characteristics are known yet.")}</p>
        )}
      </section>

      <section className="mb-6" data-testid="trail-map-section">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-display text-2xl font-bold uppercase text-white sm:text-3xl">{translateLabel("Trail map")}</h2>
            <span className="flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 text-xs font-bold text-zinc-300" data-testid="path-source-badge">
              {geometry.verified ? <CheckCircleIcon size={14} weight="fill" className="text-emerald-400" /> : <PathIcon size={14} />}
              {translateLabel(geometry.label)}{geometry.verified ? ` · ${translateLabel("verified")}` : ""}
            </span>
            {geometryProviderLabel && (
              trail.path_source_url ? (
                <a
                  href={trail.path_source_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-semibold text-zinc-400 underline decoration-zinc-600 underline-offset-4 hover:text-white"
                  data-testid="path-source-attribution"
                >
                  {translateLabel("Source: {source}", { source: geometryProviderLabel })}
                </a>
              ) : (
                <span className="text-xs font-semibold text-zinc-500" data-testid="path-source-attribution">
                  Source: {geometryProviderLabel}
                </span>
              )
            )}
          </div>

          {canManageGeometry && (
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
              <input ref={fileRef} type="file" accept=".gpx,.xml" onChange={uploadGpx} className="hidden" data-testid="gpx-file-input" />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="upload-gpx-btn"
                className="flex items-center gap-1.5 rounded-full bg-[#CCFF00] px-4 py-2 text-xs font-bold text-black transition-transform active:scale-95 disabled:opacity-60"
              >
                <UploadSimpleIcon size={14} weight="bold" /> {translateLabel(uploading ? "Uploading…" : trail.path_source === "gpx" ? "Replace verified GPX" : "Upload verified GPX")}
              </button>
              {trail.path_source === "gpx" && (
                <button
                  onClick={removeGpx}
                  data-testid="remove-gpx-btn"
                  className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-bold text-zinc-300 transition-colors hover:bg-white/10"
                >
                  <TrashIcon size={14} /> {translateLabel("Remove")}
                </button>
              )}
            </div>
          )}
        </div>

        {canManageGeometry && (
          <p className="mb-3 text-xs text-zinc-500" data-testid="gpx-admin-hint">
            {translateLabel("Admin geometry upload keeps the complete GPX track and publishes it as verified trail geometry.")}
          </p>
        )}

        {trail.path?.length ? (
          <div className="h-[320px] overflow-hidden rounded-2xl border border-white/10 bg-[#121214] sm:h-[400px] lg:h-[520px]" data-testid="trail-map">
            <MapView
              parks={[trail.park]}
              trails={[{ id: trail.id, difficulty: trail.difficulty, name: trail.name, path: trail.path }]}
              selectedId={trail.park.id}
              hoveredTrailId={trail.id}
              fitTrails
              fitKey={`${trail.path_source || "unknown"}-${trail.path.length}`}
              zoom={13}
            />
          </div>
        ) : (
          <div className="flex h-48 items-center justify-center rounded-2xl border border-dashed border-white/10 bg-[#121214] text-sm text-zinc-500" data-testid="trail-map-empty">
            {translateLabel("No trail geometry is available yet.")}
          </div>
        )}
      </section>


      <div className="mt-10">
        <CommunityMediaGallery destinationId={trail.park.id} trailId={trail.id} title={translateLabel("Trail media")} />
      </div>

      <section
        className="mt-10 rounded-2xl border border-white/10 bg-[#121214] p-5"
        data-testid="trail-community-actions"
      >
        <div className="label-tiny text-[#CCFF00]">{translateLabel("Community")}</div>
        <h2 className="mt-1 font-display text-2xl font-bold uppercase text-white">
          {translateLabel("Help improve this trail")}
        </h2>
        <p className="mt-1 text-sm leading-relaxed text-zinc-400">
          {translateLabel("Spotted outdated trail information or another problem? Help other riders by letting us know.")}
        </p>
        <div className="mt-4 space-y-3 text-xs">
          <SuggestEdit
            targetType="TRAIL"
            targetId={trail.id}
            targetName={trail.name || translateLabel("this trail")}
            compact
          />
          <ReportContent
            targetType="TRAIL"
            targetId={trail.id}
            targetName={trail.name || "this trail"}
            compact
          />
        </div>
      </section>
    </AppShell>
  );
}
